import { isCategoryVisibleNow, isOfferVisibleNow } from "@/lib/catalog-visibility";
import { Category, ItemOffer } from "@/types";
import { Atom, atom } from "jotai";
import availabilitiesAtom from "./availabilities";
import { minuteNowAtom, tenantTimezoneAtom } from "./tenant-clock";

type ItemOffersRecord = Record<string, ItemOffer>;
type CategoryItemOffersRecord = Record<string, ItemOffer[]>;

/**
 * Builds a derived atom mapping each category id to its item offers,
 * sorted by `index`. Shared by the DELIVERY and INDOOR menus, which
 * only differ in the source atoms.
 *
 * Scheduled-hours windows are applied HERE, at the single source every menu
 * surface reads from (sections, category modals, related items, promo
 * ribbons), instead of being re-derived per screen: a category outside its
 * window gets no entry at all, and an offer outside its own window is dropped
 * from its category's list. Availability is re-evaluated whenever the 60s
 * catalog poll refreshes `availabilitiesAtom`, the same cadence the screens
 * used before.
 */
export function createCategoriesItemOffersAtom(
  categoriesAtom: Atom<Category[]>,
  itemOffersAtom: Atom<ItemOffer[]>,
  hoursGateAtom: Atom<boolean> | null = null,
) {
  return atom((get) => {
    const itemOffers = get(itemOffersAtom);
    const categories = get(categoriesAtom);
    const availabilities = get(availabilitiesAtom);
        const options = {
      enforceHours: hoursGateAtom ? get(hoursGateAtom) : true,
      now: get(minuteNowAtom),
      timeZone: get(tenantTimezoneAtom),
    };

    const itemOffersMap = itemOffers.reduce<ItemOffersRecord>((map, itemOffer) => {
      map[itemOffer.id] = itemOffer;
      return map;
    }, {});

    const categoryItemOffers = categories.reduce<CategoryItemOffersRecord>((acc, category) => {
      if (!category?.itemOfferId) return acc;
      if (!isCategoryVisibleNow(category, availabilities, options)) return acc;

      const offers = category.itemOfferId
        .map((itemOfferId) => itemOffersMap[itemOfferId])
        .filter(
          (itemOffer): itemOffer is ItemOffer =>
            Boolean(itemOffer) && isOfferVisibleNow(itemOffer, availabilities, null, options),
        );

      offers.sort((a, b) => a.index - b.index);

      acc[category.id] = offers;
      return acc;
    }, {});

    return categoryItemOffers;
  });
}
